import PropTypes from 'prop-types';
import { createRef, useEffect, useRef, useState } from 'react';
import _ from 'lodash';

import { PlayArrow } from '@mui/icons-material';
import { Button } from '@mui/material';

import ReactPlayer from 'react-player';

import ReactCSSTransitionReplace from 'react-css-transition-replace';

import { FadeInAnimation, ParallaxComponent, Shapes } from '../../shared';

import FullScreenDialog from '../../shared/fulll-screen-dialog.component';

import Image from '../../shared/image.component';

import { parseTitle } from '../../../../utils/title-parser';

import useResponsive from '../../../../utils/responsive';

const appFeaturesMargin = 60;

const MobileAppFeatures = (props) => {
    const { title, features, setScrollOffsetHeight } = props;
    const { isMobile, isSmallTablet } = useResponsive();
    const [windowHeight, setWindowHeight] = useState(1000);
    const [offsetHeight, setOffsetHeight] = useState(10);
    const [featureBottomRefs, setFeatureBottomRefs] = useState([]);
    const [featureTopRefs, setFeatureTopRefs] = useState([]);
    const [activeImageKey, setActiveImageKey] = useState(null);
    const [imageHeight, setImageHeight] = useState(0);
    
    const imageRef = useRef(null);
    let debouncedScroll;

    const setFeatureRefs = () => {
        setFeatureBottomRefs((_featureBottomRefs) =>
            Array(_.size(features))
                .fill(null)
                .map((item, i) => _featureBottomRefs[i] || createRef()),
        );
        setFeatureTopRefs((_featureTopRefs) =>
            Array(_.size(features))
                .fill(null)
                .map((item, i) => _featureTopRefs[i] || createRef()),
        );
    };

    const handleResize = () => {
        if (!(isMobile || isSmallTablet)) {
            const { innerHeight } = window;
            setWindowHeight(innerHeight - appFeaturesMargin);
            if (_.has(imageRef, 'current.clientHeight')) {
                const offsetValue = innerHeight - appFeaturesMargin - imageRef.current.clientHeight;
                setOffsetHeight(offsetValue);
                setScrollOffsetHeight(offsetValue);
            }
        } else {
            setScrollOffsetHeight(0);
        }
    };

    const handleScroll = () => {
        if (isMobile || isSmallTablet) {
            return;
        }

        let imageHalfwayPoint = null;
        let imageBottom = null;

        if (imageRef?.current) {
            const imageBoundingClientRect = imageRef.current.getBoundingClientRect();
            imageHalfwayPoint = imageBoundingClientRect.top + imageBoundingClientRect.height / 2;
            imageBottom = imageBoundingClientRect.top + imageBoundingClientRect.height;
            setImageHeight(imageBoundingClientRect.height);

            _.forEach(featureBottomRefs, (featureRef, index) => {
                const refBoundingClientRect = featureRef.current.getBoundingClientRect();
                const nextRefBoundingClientRect =
                    index + 1 < _.size(featureBottomRefs) &&
                    featureTopRefs[index + 1].current.getBoundingClientRect();
                if (
                    refBoundingClientRect.bottom >= imageHalfwayPoint &&
                    refBoundingClientRect.bottom <= imageBottom
                ) {
                    setActiveImageKey(index);
                } else if (
                    nextRefBoundingClientRect.bottom <= imageHalfwayPoint &&
                    nextRefBoundingClientRect.bottom >= imageBoundingClientRect.top
                ) {
                    setActiveImageKey(index + 1);
                }
            });
        }
    };

    useEffect(() => {
        setFeatureRefs();
        setActiveImageKey(0);
    }, []);

    useEffect(() => {
        handleResize();
    }, [activeImageKey, isMobile, isSmallTablet]);

    useEffect(() => {
        debouncedScroll = _.debounce(handleScroll, 1);
        window.addEventListener('scroll', debouncedScroll, true);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('scroll', debouncedScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [featureBottomRefs, featureTopRefs]);

    const renderImage = () => {
        if (_.isNil(activeImageKey)) {
            return null;
        }        
        return (
            <Image className='mobile-app-features-image'
                key={activeImageKey}
                src={features[activeImageKey].app_feature_image.url}
                alt="phone"
                isOldImage
            />
        );
    };

    const renderSafeLivingParallaxElement = () => {
        return (
            <div style={{ opacity: activeImageKey === 0 ? 1 : 0 }}>

                <ParallaxComponent
                    src="/assets/images/fingerprint-icon.png"
                    style={{
                        position: 'absolute',
                        right: '5%',
                        top: '5%',
                    }}
                    speed={0.1}
                />

                <Shapes
                    isPair
                    isCircle
                    isOpposite
                    isSmallDark
                    speed={-0.1}
                    smallRadius={27.89}
                    largeRadius={66.39}
                    distanceBetween={{
                        width: '15%',
                        height: '20%',
                    }}
                    containerMargins={{
                        left: '5.5%',
                        top: '90%',
                    }}
                    zIndex={2}
                />

            </div>
        );
    };

    const renderSelfServiceParallaxElement = () => {
        return (
            <div style={{ opacity: activeImageKey === 1 ? 1 : 0 }}>

                <ParallaxComponent
                    src="/assets/images/key-icon.png"
                    style={{
                        position: 'absolute',
                        right: '1%',
                        top: '5%',
                    }}
                    speed={0.1}
                />

                <ParallaxComponent
                    src="/assets/images/clock-icon.png"
                    style={{
                        position: 'absolute',
                        left: '3%',
                        top: '80%',
                    }}
                    speed={-0.1}
                />

            </div>
        );
    };

    const renderUtilitiesParallaxElement = () => {
        return (
            <div style={{ opacity: activeImageKey === 2 ? 1 : 0 }}>

                <ParallaxComponent
                    src="/assets/images/wallet-icon.png"
                    style={{
                        position: 'absolute',
                        right: '0%',
                        top: '5%',
                    }}
                    speed={0.1}
                />

                <ParallaxComponent
                    src="/assets/images/water.png"
                    style={{
                        position: 'absolute',
                        left: '5%',
                        top: '70%',
                    }}
                    speed={-0.1}
                />

            </div>
        );
    };

    const renderGuestManagementParallaxElement = () => {
        return (
            <div style={{ opacity: activeImageKey === 3 ? 1 : 0 }}>

                <Shapes
                    isPair
                    isDark
                    degrees="31.44deg"
                    speed={0.1}
                    smallMargins={{ width: 50, height: 25 }}
                    largeMargins={{ width: 86, height: 43 }}
                    distanceBetween={{ width: '0%', height: '18%' }}
                    containerMargins={{ left: '80%', top: '5%' }}
                    zIndex={2}
                />

                <ParallaxComponent
                    src="/assets/images/contact-icon.png"
                    style={{
                        position: 'absolute',
                        left: '0%',
                        top: '70%',
                    }}
                    speed={-0.1}
                />

            </div>
        );
    };

    const renderHomiiAssistParallaxElement = () => {
        return (

            <div style={{ opacity: activeImageKey === 4 ? 1 : 0 }}>

                <ParallaxComponent
                    src="/assets/images/warning-icon.png"
                    style={{
                        position: 'absolute',
                        right: '-5%',
                        top: '-5%',
                    }}
                    speed={0.1}
                    max={5800}
                />

                <ParallaxComponent
                    src="/assets/images/clock-icon.png"
                    style={{
                        position: 'absolute',
                        left: '3%',
                        top: '70%',
                    }}
                    speed={-0.1}
                    min={5300}
                />

            </div>

        );
    };

    const renderParallaxElements = () => {
        return (
            <>
                {renderSafeLivingParallaxElement()}
                {renderSelfServiceParallaxElement()}
                {renderUtilitiesParallaxElement()}
                {renderGuestManagementParallaxElement()}
                {renderHomiiAssistParallaxElement()}
            </>
        );
    };

    const renderParallaxContainer = () => {
        return (
            <div className='mobile-app-features-parallax-element-container' style={{ height: imageHeight }}>
                {renderParallaxElements()}
            </div>
        );
    };

    const renderFeatures = () => {
        return _.map(features, (feature, i) => {
            const [showVideo, setShowVideo] = useState(false);
            const title = _.get(feature, 'title');
            const subHeading = _.get(feature, 'sub_heading');
            const icons = _.get(feature, 'icons');
            const videoUrl = _.get(feature, 'app_feature_video_url');

            const onWatchVideoClick = () => {
                setShowVideo(true);
            };

            const renderVideo = () => {
                if (!showVideo || !videoUrl) {
                    return null;
                }
                return (

                    <FullScreenDialog
                        key={i}
                        isOpen={showVideo}
                        setIsOpen={setShowVideo}
                    >
                        <div className='container-root container-root-maxWidthLg'>

                            <div className='mobile-app-features-video-container'>

                                <ReactPlayer
                                    className="react-player"
                                    url={videoUrl}
                                    playing={false}
                                    width="100%"
                                    height={isMobile ? 220 : 740}
                                    controls
                                />

                            </div>

                        </div>

                    </FullScreenDialog>

                );
            };

            const renderText = () => {
                return (

                    <div className='mobile-app-features-text-container' key={i}>

                        <div className='mobile-app-features-reference-container mobile-app-features-reference-container-height-1' ref={featureTopRefs[i]} />

                        <FadeInAnimation>

                            <h4 className='mobile-app-features-title'>{title}</h4>

                            <p className='mobile-app-features-sub-heading'>{subHeading}</p>

                            {renderFeatureIcons(icons)}

                            {videoUrl && (
                                <Button className="outline-button" onClick={onWatchVideoClick}>
                                    <PlayArrow />
                                    Watch how it works
                                </Button>
                            )}

                            {renderVideo()}

                        </FadeInAnimation>

                        <div className='mobile-app-features-reference-container' ref={featureBottomRefs[i]} />

                    </div>

                );
            };

            const renderItem = () => {
                return (
                    <div key={i}>
                        {(isMobile || isSmallTablet) && (
                            <div className='main-grid-item main-grid-md-6'>
                                <Image className='mobile-app-features-image'
                                    src={feature.app_feature_image.url}
                                    alt="phone"
                                    isOldImage
                                />
                            </div>
                        )}
                        {isMobile || isSmallTablet ? (
                            <div className="main-grid-item main-grid-md-6 mobile-app-features-text-grid-container">
                                {renderText()}
                            </div>
                        ) : (
                            renderText()
                        )}
                    </div>
                );
            };

            return isMobile || isSmallTablet ? (
                <div className='main-grid-container mobile-app-features-grid-container' key={i}>
                    {renderItem()}
                </div>
            ) : (
                renderItem()
            );
        });
    };

    const renderFeatureIcons = (icons) => {
        return _.map(icons, (icon, index) => {
            const iconSrc = _.get(icon, 'icon_image.url');
            const iconParagraph = _.get(icon, 'icon_paragraph');
            const iconTitle = _.get(icon, 'icon_title');
            return (

                <div key={index} className='mobile-app-features-icon-and-text-container'>

                    <div className='partners-section-icon-container'>
                        <Image src={iconSrc} alt="icon" isOldImage />
                    </div>

                    <div>
                        <h5>{iconTitle}</h5>
                        <p className='mobile-app-features-icon-description'>{iconParagraph}</p>
                    </div>

                </div>

            );
        });
    };

    return (
        <>
            <div className='mobile-app-features-heading-container'>
                {parseTitle(title, 'app_features_header')}
            </div>

            {isMobile || isSmallTablet ? (
                renderFeatures()
            ) : (

                <div className='main-grid-container mobile-app-features-grid-container'>

                    <div className='main-grid-item main-grid-md-6'
                        style={{ 
                            position: 'sticky',
                            top: appFeaturesMargin,
                            height: windowHeight
                        }}
                    >

                        <div className='mobile-app-features-image-transition-container' ref={imageRef}>

                            <ReactCSSTransitionReplace
                                transitionName="cross-fade"
                                transitionEnterTimeout={650}
                                transitionLeaveTimeout={650}
                            >
                                {renderImage()}
                            </ReactCSSTransitionReplace>

                        </div>

                        {renderParallaxContainer()}

                    </div>

                    <div className='main-grid-item main-grid-md-6 mobile-app-features-text-grid-container'
                        style={{                           
                            marginBottom: offsetHeight                            
                         }}
                    >
                        {renderFeatures()}
                    </div>

                </div>

            )}
        </>
    );
};

export default MobileAppFeatures;

MobileAppFeatures.propTypes = {
    title: PropTypes.string,
    features: PropTypes.array,

    setScrollOffsetHeight: PropTypes.func,
};

